import {
  Checkbox,
  Column,
  Input,
  PhoneNumber,
  CountryCode,
  Select,
  ValueOpt,
  Row,
  Label,
  useDarkMode,
  AlertConstants,
  useAlerts,
} from "best-common-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useDropdowns } from "../../contexts/DropdownsContext";
import { CreditCardType } from "../../types/CreditCardType";
import { Month } from "../../types/Months";
import { Subdivision } from "../../types/Subdivision";
import { BillingDTO, UserBillingCountry } from "../../types/UserBilling";
import { StatedRequiredCountryIds, validCreditCard } from "../../util/CreditCardUtil";
import { decodeJwt } from "jose";
import { useAuth } from "../../contexts/AuthContext";
import { getCaptureContext } from "../../api/CyberSourceApi";

type StyleProps = {
  stacked: boolean;
};

const LeftDefault = styled.div<StyleProps>`
  margin-top: var(--spacing-2);
  display: ${(props: StyleProps) => (props.stacked ? "none" : "block")};
  @media (max-width: 992px) {
    display: none;
  }
`;

const BottomDefault = styled.div<StyleProps>`
  margin-top: var(--spacing-2);
  @media (min-width: 992px) {
    display: ${(props: StyleProps) => (props.stacked ? "block" : "none")};
  }
`;

// Helpers
const decodeJwtPayload = (jwt: string): any => {
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(payload);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const extractMicroformConfig = (jwt: string): { clientLibrary?: string; clientLibraryIntegrity?: string } => {
  const payload = decodeJwtPayload(jwt);
  const ctxArray = payload?.ctx;
  const node = Array.isArray(ctxArray) && ctxArray.length > 0 && ctxArray[0]?.data ? ctxArray[0].data : payload?.data;
  return {
    clientLibrary: node?.clientLibrary,
    clientLibraryIntegrity: node?.clientLibraryIntegrity,
  };
};

const loadScriptFromConfig = (clientLibrary: string, clientLibraryIntegrity?: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const existing = Array.from(document.getElementsByTagName("script")).find((s) => s.src === clientLibrary);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = clientLibrary;
    script.async = true;
    script.defer = true;
    if (clientLibraryIntegrity) {
      script.integrity = clientLibraryIntegrity;
      script.crossOrigin = "anonymous";
    }
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Cybersource Microform"));
    document.head.appendChild(script);
  });

const createTokenPromise = (micro: any, expMonth: string, expYear: string): Promise<any> =>
  new Promise((resolve, reject) => {
    try {
      const tokenResponseHandler = (error: any, response?: any) => {
        if (typeof response !== "undefined") {
          if (error) {
            reject(error);
          } else {
            resolve(response);
          }
        } else {
          resolve(response);
        }
      };
      micro.createToken({ expirationMonth: expMonth, expirationYear: expYear }, tokenResponseHandler);
    } catch (err) {
      reject(err);
    }
  });

type CreditCardFormProps = {
  cc: BillingDTO;
  setCC: (value: BillingDTO) => void;
  registerCreateToken: (value: () => Promise<any>) => void;
  setValid: (value: boolean) => void;
  stacked?: boolean;
};

const CreditCardForm = ({ cc, setCC, registerCreateToken, stacked = false, setValid }: CreditCardFormProps) => {
  const { userInfo } = useAuth();
  const { isDarkMode } = useDarkMode();
  const { addAlert } = useAlerts();
  const {
    cardTypeOptions,
    stateOptions,
    monthNumberOptions: monthOptions,
    creditYearOptions,
    billingCountryOptions,
  } = useDropdowns();
  const [captureContext, setCaptureContext] = useState<string | null>(null);
  const [validPhoneNumber, setValidPhoneNumber] = useState<boolean>(false);
  const [validCardNumber, setValidCardNumber] = useState<boolean>(false);
  const [validSecurityCode, setValidSecurityCode] = useState<boolean>(false);

  const microformRef = useRef<any>(null);
  const securityCodeRef = useRef<any>(null);
  const numberFieldRef = useRef<any>(null);

  const displayedStateOptions: ValueOpt<Subdivision>[] = useMemo(
    () => (!!cc?.countryId ? stateOptions.filter((state) => state.value.countryId === cc.countryId) : stateOptions),
    [stateOptions, cc.countryId],
  );

  const handleChange = (type: keyof BillingDTO, value: number | string | boolean | CreditCardType): void => {
    setCC({ ...cc, [type]: value });
  };

  const handleExpirationChange = (type: "month" | "year", value: string | number) => {
    let [month, year] = cc.ccExp.length > 0 ? cc.ccExp.split("/") : ["", ""];
    if (type === "month") month = value?.toString();
    if (type === "year") year = value?.toString();
    setCC({ ...cc, ccExp: `${month}/${year}` });
  };

  const defaultCardCheckbox = (
    <Checkbox
      id="isDefault"
      checked={cc.isDefault}
      label={<span style={{ fontSize: "0.875rem", fontWeight: "bold" }}>Set as default payment method</span>}
      onChange={() => {
        handleChange("isDefault", !cc.isDefault);
      }}
    />
  );

  const month: ValueOpt<Month> | undefined = useMemo(
    () =>
      monthOptions.find(
        (option: ValueOpt<Month>) => option.value.number === (cc.ccExp?.length > 0 ? cc.ccExp.split("/")[0] : ""),
      ),
    [monthOptions, cc.ccExp],
  );
  const year: ValueOpt<number> | undefined = useMemo(
    () =>
      creditYearOptions.find(
        (option: ValueOpt<number>) => option.value === (cc.ccExp?.length > 1 ? parseInt(cc.ccExp.split("/")[1]) : ""),
      ),
    [creditYearOptions, cc.ccExp],
  );
  const cardType: ValueOpt<CreditCardType> | undefined = useMemo(
    () => (cc.ccType ? cardTypeOptions.find((option) => option.value.ccTypeId === cc.ccType.ccTypeId) : undefined),
    [cardTypeOptions, cc.ccType],
  );
  const billingCountry: ValueOpt<UserBillingCountry> | undefined = useMemo(
    () =>
      cc.countryId
        ? billingCountryOptions.find((option) => option.value.userBillingCountryId === cc.countryId)
        : undefined,
    [billingCountryOptions, cc.countryId],
  );
  const state: ValueOpt<Subdivision> | undefined = useMemo(
    () => (cc.state ? displayedStateOptions.find((option) => option.value.name === cc.state) : undefined),
    [displayedStateOptions, cc.state],
  );

  // Effect 1: Load Microform library script from captureContext JWT
  const [scriptReady, setScriptReady] = useState<boolean>(false);
  useEffect(() => {
    setScriptReady(false);
    if (!captureContext) return;
    const { clientLibrary, clientLibraryIntegrity } = extractMicroformConfig(captureContext);
    if (!clientLibrary) return;
    loadScriptFromConfig(clientLibrary, clientLibraryIntegrity)
      .then(() => setScriptReady(true))
      .catch(() => setScriptReady(false));
  }, [captureContext]);

  // Effect 2: Initialize Microform and mount fields
  const [microReady, setMicroReady] = useState<boolean>(false);
  useEffect(() => {
    setMicroReady(false);
    if (!scriptReady || !captureContext) return;
    const flex = new (window as any).Flex(captureContext);
    const microFormStyles = {
      input: {
        "font-size": "16px",
        color: isDarkMode ? " #FFFFFF" : "#000000",
      },
      "::placeholder": {
        color: "#ccc",
      },
    };
    const microform = flex.microform("card", {
      styles: microFormStyles,
    });
    microformRef.current = microform;

    const numberField = microform.createField("number", {
      placeholder: "Credit Card Number",
      "aria-required": true,
    });
    numberField.on("change", (e: any) => setValidCardNumber(e.valid || e.couldBeValid));
    const securityCodeField = microform.createField("securityCode", {
      placeholder: "CVV",
      "aria-required": true,
    });
    securityCodeField.on("change", (e: any) => setValidSecurityCode(e.valid || e.couldBeValid));
    numberFieldRef.current = numberField;
    securityCodeRef.current = securityCodeField;
    numberField.load("#microform-number");
    securityCodeField.load("#microform-security-code");
    setMicroReady(true);

    return () => {
      try {
        numberField.unload();
      } catch {}
      try {
        securityCodeField.unload();
      } catch {}
      microformRef.current = null;
      setMicroReady(false);
    };
  }, [scriptReady, captureContext, isDarkMode]);

  // Effect 3: Register createToken handler for parent when microform is ready
  useEffect(() => {
    if (!registerCreateToken || !microReady || !microformRef.current) return;
    registerCreateToken(async () => {
      let [month, year] = (cc.ccExp || "").split("/");
      month = (month || "").toString().padStart(2, "0");
      year = (year || "").toString();
      const res: any = await createTokenPromise(microformRef.current, month, year);
      const token: string = typeof res === "string" ? res : res?.token;
      if (!token) {
        addAlert({
          type: AlertConstants.TYPES.DANGER,
          text: "Error validating credit card details, please verify the card details and try again",
        });
        throw new Error("Microform did not return a token");
      }
      // best-effort last4 from response if present
      const parsedToken: any = await decodeJwt(token);
      let last4: string | undefined;
      try {
        const masked = parsedToken?.content?.paymentInformation?.card?.number?.maskedValue;
        if (masked && typeof masked === "string") {
          const digits = masked.replace(/\D/g, "");
          last4 = digits.slice(-4);
        }
      } catch {}
      const countryOpt: ValueOpt<UserBillingCountry> | undefined = cc?.countryId
        ? billingCountryOptions.find(
            (opt: ValueOpt<UserBillingCountry>) => opt?.value?.userBillingCountryId === cc.countryId,
          )
        : undefined;
      const countryCode: string | undefined = countryOpt?.value?.code;
      const email: string | undefined = userInfo?.email;
      return {
        transientToken: token,
        ccLast4: last4,
        // @ts-ignore
        ccType: cc.ccType ? cc.ccType.name : undefined,
        ccExp: cc.ccExp,
        countryCode,
        email,
      };
    });
  }, [registerCreateToken, microReady, cc.ccExp, cc.ccType]);

  // Effect 4: Get capture context
  useEffect(() => {
    getCaptureContext().then(setCaptureContext);
  }, []);

  // Effect 5: Set valid state
  useEffect(() => {
    setValid(
      !!(
        scriptReady &&
        microReady &&
        !!captureContext &&
        validCreditCard(cc) &&
        validPhoneNumber &&
        validCardNumber &&
        validSecurityCode
      ),
    );
  }, [scriptReady, microReady, captureContext, cc, validPhoneNumber, validCardNumber, validSecurityCode]);

  return (
    <Row>
      <Column width={stacked ? 1 : 2}>
        <Row>
          <Column width={2}>
            <Select
              id="cardType"
              required
              gutterBottom
              label="Card Type"
              placeholder="Select Card Type"
              options={cardTypeOptions}
              value={cardType}
              onChange={({ value }: ValueOpt<CreditCardType>) => handleChange("ccType", value)}
            />
          </Column>
          <Column width={stacked ? 1 : 2}>
            <Input
              id="cardNickname"
              label="Card Nickname"
              value={cc.ccNickname}
              onChange={(value: string) => handleChange("ccNickname", value)}
              required
              gutterBottom
            />
          </Column>
        </Row>
        <Row>
          <Column width={1}>
            <div className="bcr-mb-2">
              <Label htmlFor="microform-number" required>
                Card Number
              </Label>
              <div id="microform-number" className="microform-input" data-invalid={!validCardNumber} />
            </div>
          </Column>
        </Row>
        <Row>
          <Column width={4}>
            <Select
              id="expDate"
              label="Exp Month"
              required
              gutterBottom
              options={monthOptions}
              value={month}
              onChange={({ value }: ValueOpt<Month>) => handleExpirationChange("month", value.number)}
              placeholder="Month"
            />
          </Column>
          <Column width={4}>
            <Select
              id="expDateYear"
              required
              gutterBottom
              label="Exp Year"
              options={creditYearOptions}
              value={year}
              onChange={(value: ValueOpt<number>) => handleExpirationChange("year", value.value)}
              placeholder="Year"
            />
          </Column>
          <Column width={2}>
            <div className="bcr-mb-2">
              <Label htmlFor="microform-security-code" required>
                CVV
              </Label>
              <div id="microform-security-code" className="microform-input" data-invalid={!validSecurityCode} />
            </div>
          </Column>
        </Row>
        <LeftDefault stacked={stacked}>{defaultCardCheckbox}</LeftDefault>
      </Column>
      <Column width={stacked ? 1 : 2}>
        <Row>
          <Column width={2}>
            <Input
              required
              id="billingFirstName"
              label="Billing First Name"
              value={cc.firstName}
              onChange={(value: string) => handleChange("firstName", value)}
              gutterBottom
            />
          </Column>
          <Column width={2}>
            <Input
              label="Billing Last Name"
              id="billingLastName"
              required
              value={cc.lastName}
              onChange={(value: string) => handleChange("lastName", value)}
              gutterBottom
            />
          </Column>
          <Column width={1}>
            <Select
              id="billingCountry"
              label="Billing Country"
              required
              gutterBottom
              options={billingCountryOptions}
              value={billingCountry}
              onChange={(value: ValueOpt<UserBillingCountry>) =>
                setCC({ ...cc, countryId: value.value.userBillingCountryId, state: "", phone: "" })
              }
            />
          </Column>
          <Column width={1}>
            <Input
              label="Billing Address"
              id="billingAddress"
              required
              value={cc.addressLine1}
              onChange={(value: string) => handleChange("addressLine1", value)}
              gutterBottom
            />
            <Input
              id="addressLine2"
              value={cc.addressLine2}
              onChange={(value) => handleChange("addressLine2", value)}
              gutterBottom
            />
          </Column>
          <Column width={1}>
            <Input
              id="city"
              label="City"
              required
              gutterBottom
              value={cc.city}
              onChange={(value: string) => handleChange("city", value)}
            />
          </Column>
          <Column width={2}>
            <Select
              id="state"
              label="State"
              required={StatedRequiredCountryIds.includes(cc?.countryId)}
              gutterBottom
              options={displayedStateOptions}
              value={state}
              onChange={(value) => handleChange("state", value?.value?.name ?? "")}
              clearable
            />
          </Column>
          <Column width={2}>
            <Input
              id="zipcode"
              required
              label="Zip Code"
              value={cc.zip}
              onChange={(value: string) => handleChange("zip", value)}
            />
          </Column>
          <Column width={1}>
            <PhoneNumber
              key={billingCountry?.value?.code ?? cc.countryId}
              id="phoneNumber"
              label="Phone Number"
              required
              className="credit-card-phone"
              value={cc.phone}
              onChange={(value: string) => handleChange("phone", value)}
              gutterBottom
              setValidPhoneNumber={setValidPhoneNumber}
              countrySelect={{
                enabled: true,
                initialValue: billingCountry?.value?.code as CountryCode,
              }}
            />
          </Column>
        </Row>
        <BottomDefault stacked={stacked}>{defaultCardCheckbox}</BottomDefault>
      </Column>
    </Row>
  );
};

export default CreditCardForm;
